import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ButtonClick, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import { every, uniq } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { AuthenticationService } from './authentication/authentication.service';
import { User } from '../models/vnext.models';

export interface HasLockInfo {
    idLockedUser: string;
    locked: boolean;
    lockedHost: string;
    lockedUserFullName: string;
}

export interface HasExaminationState extends HasLockInfo {
    isApproved: boolean;
    isDigitallySigned: boolean;
    isExecuted: boolean;
    isReported: boolean;
    addendumNumber?: number;
    referringPhysician?: User;
}

@Injectable({
    providedIn: 'root',
})
export class ExaminationUtilitiesService {
    constructor(
        private authenticationService: AuthenticationService,
        private dialogService: ModalDialogNotificationService,
        private translate: TranslateService
    ) {}

    async confirmUnlockByModal(items: HasLockInfo[]) {
        const currentUserId = this.authenticationService.getUserId();
        const lockedByUserId = uniq(items.map((i) => i.idLockedUser));
        if (lockedByUserId.every((userId) => userId === currentUserId)) {
            return true;
        }

        const lockedByUserName = uniq(items.map((i) => i.lockedUserFullName));
        const warningMessage =
            lockedByUserName.length === 1
                ? this.translate.instant('WarningUnlock', { p0: lockedByUserName[0] })
                : this.translate.instant('WarningUnlockMultipleUsers', { p0: lockedByUserName.join(', ') });

        const button = await firstValueFrom(
            this.dialogService.notify({
                message: warningMessage,
                title: this.translate.instant('Warning'),
                type: NotificationType.warning,
                buttonCancel: false,
                buttonNo: true,
                buttonYes: true,
                buttonOk: false,
            })
        );

        return button === ButtonClick.yes;
    }

    /**
     * Si possono rendere definitive le prestazioni:
     *   - Non bloccate.
     *   - In stato R.
     *   - Non già rese definitive o firmate.
     */
    isApprovable(arg: HasExaminationState | HasExaminationState[]) {
        const examinations = arg instanceof Array ? arg : [arg];
        if (!this.verifyLockInfo(examinations)) {
            return false;
        }

        return every(examinations, (ex) => ex.isReported && !ex.isApproved && !ex.isDigitallySigned); // && ex.referringPhysician
    }

    /**
     * Sono editabili le prestazioni:
     *   - Non bloccate.
     *   - In stato E.
     *   - Non già rese definitive o firmate.
     */
    isEditable(arg: HasExaminationState | HasExaminationState[]) {
        const examinations = arg instanceof Array ? arg : [arg];
        if (!this.verifyLockInfo(examinations)) {
            return false;
        }

        return every(examinations, (ex) => ex.isExecuted && !ex.isApproved && !ex.isDigitallySigned);
    }

    /**
     * Sono firmabili le prestazioni:
     *   - Non bloccate.
     *   - In stato R | D .
     *   - Non già firmate.
     */
    isSignable(arg: HasExaminationState | HasExaminationState[]) {
        const examinations = arg instanceof Array ? arg : [arg];
        if (!this.verifyLockInfo(examinations)) {
            return false;
        }

        return every(examinations, (ex) => (ex.isReported || ex.isApproved) && !ex.isDigitallySigned);
    }

    /**
     * Sì può rimuovere la validazione da prestazioni:
     *   - Non bloccate.
     *   - Definitive o Firmate.
     */
    isValidationUndoable(arg: HasExaminationState | HasExaminationState[]) {
        const examinations = arg instanceof Array ? arg : [arg];
        if (!this.verifyLockInfo(examinations)) {
            return false;
        }

        if ((examinations.length === 1)) {
            return (examinations[0].isApproved || examinations[0].isDigitallySigned) && !examinations[0].addendumNumber;
        }
        else{
            return every(examinations, (ex) => (ex.isApproved || ex.isDigitallySigned) && !ex.addendumNumber && ex.referringPhysician);
        }
    }


    /**
     * Verifica che le prestazioni siano bloccate dall'utente loggato su questa macchina.
     */
    verifyLockInfo(arg: HasLockInfo | HasLockInfo[]) {
        const examinations = arg instanceof Array ? arg : [arg];
        const sessionInfo = this.authenticationService.getSessionInfo();
        const userId = this.authenticationService.getUserId();

        return every(examinations, (ex) => {
            if (!ex.locked) {
                return true;
            }

            return ex.idLockedUser === userId && ex.lockedHost === sessionInfo.hostName;
        });
    }
}
